/**
 * Performance monitoring utilities for image generation page
 * Tracks loading times, cache performance, and user interactions
 */

interface PerformanceMetrics {
  imageLoadTime: number;
  cacheHitRate: number;
  totalImagesLoaded: number;
  averageLoadTime: number;
  lazyLoadingEfficiency: number;
}

interface LoadingEvent {
  url: string;
  startTime: number;
  endTime?: number;
  fromCache: boolean;
  size?: number;
}

class PerformanceMonitor {
  private loadingEvents: Map<string, LoadingEvent> = new Map();
  private metrics: PerformanceMetrics = {
    imageLoadTime: 0,
    cacheHitRate: 0,
    totalImagesLoaded: 0,
    averageLoadTime: 0,
    lazyLoadingEfficiency: 0,
  };

  /**
   * Start tracking an image load
   */
  startImageLoad(url: string, fromCache: boolean = false): void {
    const startTime = performance.now();
    this.loadingEvents.set(url, {
      url,
      startTime,
      fromCache,
    });
  }

  /**
   * End tracking an image load
   */
  endImageLoad(url: string, success: boolean = true): void {
    const event = this.loadingEvents.get(url);
    if (!event) return;

    const endTime = performance.now();
    event.endTime = endTime;

    if (success) {
      this.updateMetrics(event);
    }

    this.loadingEvents.delete(url);
  }

  /**
   * Track lazy loading intersection
   */
  trackLazyLoadIntersection(url: string, wasVisible: boolean): void {
    if (wasVisible) {
      // Image was loaded when it became visible (good lazy loading)
      this.metrics.lazyLoadingEfficiency += 1;
    }
  }

  /**
   * Update performance metrics
   */
  private updateMetrics(event: LoadingEvent): void {
    if (!event.endTime) return;

    const loadTime = event.endTime - event.startTime;
    this.metrics.totalImagesLoaded += 1;
    
    // Update average load time
    const totalTime = this.metrics.averageLoadTime * (this.metrics.totalImagesLoaded - 1) + loadTime;
    this.metrics.averageLoadTime = totalTime / this.metrics.totalImagesLoaded;

    // Update cache hit rate
    const cacheHits = event.fromCache ? 1 : 0;
    const totalCacheAttempts = this.metrics.totalImagesLoaded;
    this.metrics.cacheHitRate = (this.metrics.cacheHitRate * (totalCacheAttempts - 1) + cacheHits) / totalCacheAttempts;
  }

  /**
   * Get current performance metrics
   */
  getMetrics(): PerformanceMetrics {
    return { ...this.metrics };
  }

  /**
   * Get formatted performance report
   */
  getPerformanceReport(): string {
    const metrics = this.getMetrics();
    return `
Performance Report:
- Total Images Loaded: ${metrics.totalImagesLoaded}
- Average Load Time: ${metrics.averageLoadTime.toFixed(2)}ms
- Cache Hit Rate: ${(metrics.cacheHitRate * 100).toFixed(1)}%
- Lazy Loading Efficiency: ${metrics.lazyLoadingEfficiency} intersections
    `.trim();
  }

  /**
   * Reset all metrics
   */
  reset(): void {
    this.loadingEvents.clear();
    this.metrics = {
      imageLoadTime: 0,
      cacheHitRate: 0,
      totalImagesLoaded: 0,
      averageLoadTime: 0,
      lazyLoadingEfficiency: 0,
    };
  }

  /**
   * Log performance metrics to console (development only)
   */
  logMetrics(): void {
    if (process.env.NODE_ENV === 'development') {
      console.group('🚀 Image Performance Metrics');
      console.log(this.getPerformanceReport());
      console.groupEnd();
    }
  }

  /**
   * Track Core Web Vitals related to images
   */
  trackCoreWebVitals(): void {
    if (typeof window === 'undefined') return;

    // Track Largest Contentful Paint (LCP)
    if ('PerformanceObserver' in window) {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const lastEntry = entries[entries.length - 1];
        
        if (lastEntry && (lastEntry as any).element?.tagName === 'IMG') {
          console.log('LCP Image:', lastEntry.startTime);
        }
      });

      observer.observe({ entryTypes: ['largest-contentful-paint'] });
    }

    // Track Cumulative Layout Shift (CLS) from images
    if ('PerformanceObserver' in window) {
      const observer = new PerformanceObserver((list) => {
        let clsValue = 0;
        
        for (const entry of list.getEntries()) {
          if (!(entry as any).hadRecentInput) {
            clsValue += (entry as any).value;
          }
        }
        
        if (clsValue > 0) {
          console.log('CLS from images:', clsValue);
        }
      });

      observer.observe({ entryTypes: ['layout-shift'] });
    }
  }

  /**
   * Measure image decode time
   */
  async measureImageDecode(imageElement: HTMLImageElement): Promise<number> {
    const startTime = performance.now();
    
    try {
      await imageElement.decode();
      const endTime = performance.now();
      return endTime - startTime;
    } catch (error) {
      console.warn('Image decode failed:', error);
      return -1;
    }
  }

  /**
   * Check if image is in viewport
   */
  isImageInViewport(imageElement: HTMLImageElement): boolean {
    const rect = imageElement.getBoundingClientRect();
    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
  }

  /**
   * Get memory usage information (if available)
   */
  getMemoryUsage(): any {
    if ('memory' in performance) {
      return {
        usedJSHeapSize: (performance as any).memory.usedJSHeapSize,
        totalJSHeapSize: (performance as any).memory.totalJSHeapSize,
        jsHeapSizeLimit: (performance as any).memory.jsHeapSizeLimit,
      };
    }
    return null;
  }
}

// Export singleton instance
export const performanceMonitor = new PerformanceMonitor();

// Auto-start Core Web Vitals tracking in development
if (process.env.NODE_ENV === 'development') {
  performanceMonitor.trackCoreWebVitals();
}